/**
 * StepReformatage — wizard steps 1.3 + 1.4 (Phase 1).
 *
 * Calls IA to improve the CV phrasing (generic ATS optimization — no job offer needed).
 * Shows CvDiffViewer so the user can accept/reject each change.
 * Displays before/after ATS scores once changes are applied.
 * Fully skippable.
 */

import { useCallback, useState } from 'react';
import { Loader2, Sparkles, ChevronRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { CvDiffViewer } from '../../SimpleMode/CvDiffViewer';
import { analyzeCV } from '../../../lib/ats-analyzer';
import { cvDataToText } from '../../../lib/cv-utils';
import type { CVData } from '../../../types';

interface StepReformatageProps {
  data: CVData;
  setData: (data: CVData) => void;
}

type ReformatState = 'idle' | 'loading' | 'done' | 'applied' | 'error';

// Generic job description for "just polish this CV" mode
const GENERIC_JD =
  "Optimise ce CV pour maximiser la compatibilité ATS. Améliore la formulation " +
  "des expériences avec des verbes d'action et des résultats mesurables. Renforce " +
  "le profil professionnel. Assure la cohérence et la clarté globales.";

export function StepReformatage({ data, setData }: StepReformatageProps) {
  const [state, setState] = useState<ReformatState>('idle');
  const [reformatted, setReformatted] = useState<CVData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scoreBefore, setScoreBefore] = useState<number | null>(null);
  const [scoreAfter, setScoreAfter] = useState<number | null>(null);

  const handleStart = useCallback(async () => {
    setState('loading');
    setError(null);
    const scorePre = analyzeCV(data).score;
    setScoreBefore(scorePre);
    try {
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          route: 'ats-optimize',
          action: 'generate',
          resumeText: cvDataToText(data),
          jobDescription: GENERIC_JD,
          cvData: data,
          sectionConfig: {
            experiences: 'modifier_description',
            profil: 'modifier',
            skills: 'conserver',
            formations: 'conserver',
            langues: 'conserver',
            passions: 'conserver',
          },
          adaptationLevel: {
            experiences: 'fidele',
            profil: 'fidele',
          },
        }),
      });
      const json = await res.json() as { data?: CVData; changesSummary?: string; error?: string };
      if (!res.ok || !json.data) {
        throw new Error(json.error || "Erreur lors du reformatage");
      }
      setReformatted(json.data);
      setState('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setState('error');
    }
  }, [data]);

  const handleApply = useCallback((merged: CVData) => {
    setData(merged);
    const scorePost = analyzeCV(merged).score;
    setScoreAfter(scorePost);
    setState('applied');
  }, [setData]);

  const handleCancel = useCallback(() => {
    setState('idle');
    setReformatted(null);
  }, []);

  return (
    <div className="space-y-8">
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center size-12 rounded-full bg-violet-500/20 text-violet-400 mb-2">
          <Sparkles className="size-6" />
        </div>
        <h1 className="text-3xl font-bold tracking-tight">Reformater avec l'IA</h1>
        <p className="text-base text-white/60 max-w-xl mx-auto">
          L'IA va améliorer la formulation de vos expériences et de votre profil.
          Vous verrez exactement ce qui change avant d'accepter.
        </p>
      </div>

      {/* Idle — start CTA */}
      {state === 'idle' && (
        <div className="flex flex-col items-center gap-4">
          <button
            type="button"
            onClick={handleStart}
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-base font-bold bg-violet-600 text-white hover:bg-violet-500 shadow-xl shadow-violet-600/25 transition-all"
          >
            <Sparkles className="size-5" />
            Optimiser avec l'IA
            <ChevronRight className="size-5" />
          </button>
        </div>
      )}

      {/* Loading */}
      {state === 'loading' && (
        <div className="flex flex-col items-center gap-3 py-8">
          <Loader2 className="size-8 animate-spin text-violet-400" />
          <p className="text-sm text-white/60">L'IA reformate votre CV… (~15 secondes)</p>
        </div>
      )}

      {/* Error */}
      {state === 'error' && (
        <div className="space-y-4">
          <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
            <AlertCircle className="size-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
          <div className="flex justify-center">
            <button
              type="button"
              onClick={handleStart}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-white/10 hover:bg-white/15 transition-colors"
            >
              Réessayer
            </button>
          </div>
        </div>
      )}

      {/* Diff viewer */}
      {state === 'done' && reformatted && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-sm font-semibold text-white/70">
              Voici ce que l'IA propose de modifier — cochez ce que vous voulez accepter
            </p>
          </div>
          <CvDiffViewer
            before={data}
            after={reformatted}
            onApply={handleApply}
            onCancel={handleCancel}
          />
        </div>
      )}

      {/* Applied — score comparison */}
      {state === 'applied' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-sm">
            <CheckCircle2 className="size-4 shrink-0" />
            Modifications appliquées avec succès.
          </div>
          {scoreBefore !== null && scoreAfter !== null && (
            <div className="flex items-center justify-center gap-6 p-4 rounded-xl bg-white/[0.02] border border-white/10">
              <div className="text-center">
                <div className="text-2xl font-black text-white/40">{scoreBefore}</div>
                <div className="text-xs text-white/30">Avant</div>
              </div>
              <div className="text-2xl text-white/20">→</div>
              <div className="text-center">
                <div className={`text-2xl font-black ${scoreAfter >= scoreBefore ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {scoreAfter}
                </div>
                <div className="text-xs text-white/30">Après</div>
              </div>
              {scoreAfter > scoreBefore && (
                <div className="text-xs font-semibold text-emerald-400 bg-emerald-500/20 px-2 py-1 rounded-full">
                  +{scoreAfter - scoreBefore} pts
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
